import { Component, EventEmitter, Input, Output } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { SortDirection } from "@app/enum/SortDirection";
import { IPeripheral, IUnit } from "@app/interfaces/IPeripheral";
import { ISim } from "@app/interfaces/ISim";
import { ISimMapping } from "@app/interfaces/ISimMapping";
import { FoundationNavigationModule } from "@app/main/modules/foundation/foundation.navigation";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { SimMappingService } from "@app/main/modules/foundation/_services/sim-mapping.service";
import { UnitsService } from "@app/main/modules/foundation/_services/unit.service";
import { AccountFeatureService } from "@app/services/accountFeature.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, Observable, of } from "rxjs";
import { map, mergeMap, tap } from "rxjs/operators";
import './sim-detail.component.scss';

@UntilDestroy()
@Component({
    selector: 'sim-detail',
    template: require('./sim-detail.component.html')
})
export class SimDetailComponent {
    @Input() sim: ISim;

    @Output() mappingUpdate = new EventEmitter<ISimMapping>();

    allMappings: ISimMapping[];
    currentMapping: ISimMapping;
    mappedHardware: IPeripheral;

    isPeripheral: boolean;

    tableColumns: IVTableColumn<ISimMapping>[];
    tableRows: IVTableRow[];

    loading: boolean = true;

    constructor(
        private readonly accountFeatureService: AccountFeatureService,
        private readonly navigationService: NavigationService,
        private readonly peripheralService: PeripheralService,
        private readonly simMappingService: SimMappingService,
        private readonly translationService: TranslationService,
        private readonly unitService: UnitsService,
        private readonly vTableService: VTableService
    ) { }

    ngOnChanges(): void {
        this.createTable();
        this.getMappingForSelectedSim();
    }


    goToUnitOrPeripheral(): void {
        let isCamera: boolean;
        let navigationPath: string = FoundationNavigationModule.Units;
        let params: any = { unitSerial: this.mappedHardware.Serial };

        if (this.isPeripheral) {
            isCamera = this.accountFeatureService.featureFlagsHaveFeatureAccess(this.mappedHardware.Product?.Features, AccountFeatures.Camera)
            navigationPath = isCamera ? FoundationNavigationModule.Cameras : FoundationNavigationModule.Peripherals;
            params = { serial: this.mappedHardware.Serial }
        }

        this.navigationService.go(`${navigationPath}.view`, params);
    }

    goToVehicle(): void {
        if (!this.mappedHardware?.vehicleMapping) {
            return;
        }

        this.navigationService.go(FoundationNavigationModule.VehicleView, { vehicleId: this.mappedHardware.vehicleMapping.VehicleId });
    }

    unassignSim(confirm: boolean): void {
        if (!confirm) {
            return;
        }

        this.loading = true;

        this.simMappingService.endSimMapping(this.currentMapping).pipe(
            untilDestroyed(this),
            tap(() => {
                this.allMappings.find(mapping => mapping.Id == this.currentMapping.Id).EndUtc = new Date().toISOString();
                this.clearAllSimData();
                this.mappingUpdate.emit(null);
                this.createSimHistoryTableRows();
            })
        ).subscribe({
            complete: () => this.loading = false
        });
    }

    //--

    private getMappingForSelectedSim(): void {
        this.loading = true;

        this.simMappingService.getSimMappings({ SimId: this.sim.Id }).pipe(
            untilDestroyed(this),
            mergeMap(mappings => {
                this.allMappings = mappings;
                this.currentMapping = mappings.find(mapping => !mapping.EndUtc);
                this.mappingUpdate.emit(this.currentMapping)

                this.createSimHistoryTableRows();

                return this.currentMapping ? this.peripheralService.getPeripheralBySerial(this.currentMapping.Serial) : EMPTY;
            }),
            mergeMap(peripheral => {
                if (!this.currentMapping) {
                    return EMPTY;
                }

                if (peripheral) {
                    this.isPeripheral = true;
                    return of(peripheral);
                }

                return this.getMappedUnit();
            })
        )
            .subscribe({
                next: hardware => {
                    this.mappedHardware = hardware;
                },
                complete: () => this.loading = false,
                error: () => this.loading = false
            })
    }

    private getMappedUnit(): Observable<IUnit> {
        return this.unitService.getUnitBySerial(this.currentMapping.Serial).pipe(
            map(searchResponse => {
                return searchResponse.Unit;
            })
        )
    }

    private clearAllSimData(): void {
        this.currentMapping = null;
        this.mappedHardware = null;
    }

    private createSimHistoryTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.allMappings, this.tableColumns);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Serial" }),
                createCellFunction: simMapping => this.vTableService.createTableCell(simMapping.Serial)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Start" }),
                createCellFunction: simMapping => this.vTableService.createDateCell(simMapping.StartUtc)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.End", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: simMapping => {
                    if (simMapping.EndUtc) {
                        return this.vTableService.createDateCell(simMapping.EndUtc)
                    }

                    return this.vTableService.createTableCell(this.translationService.t("FoundationHardware.Current"));
                }
            }
        ];
    }
}
